import React, { useState, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getTranslatedProductName, getTranslatedCategory } from '../../utils/translationHelpers';
import { 
  User, 
  MapPin, 
  Package, 
  ShoppingCart, 
  Search, 
  Filter, 
  Star,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Flower
} from 'lucide-react';
import {
  OptimizedFormContainer,
  OptimizedFormSection,
  OptimizedFormGrid,
  OptimizedProductGrid,
  OptimizedProductCard,
  OptimizedOrderSummary,
  OptimizedSubmitButton,
  SearchAndFilterContainer,
  SearchInput,
  FilterSelect,
  CollapsibleSection,
  CategoryBadge,
  SectionTitle,
  FieldContainer,
  QuantityControl,
  SuccessMessage,
  ErrorMessage,
  LoadingSpinner,
  GlobalOrderSummary
} from './shared/OptimizedFormComponents';
import {
  CustomerInformationSection,
  OrderInformationSection,
  SharedOrderFormData,
  getMockCustomerData
} from './shared/SharedFormSections';
import { useCustomerData } from './shared/CustomerDataService';
import { useGlobalOrderData, OrderItem } from '../../hooks/useGlobalOrderData';
import { FormStatusIndicator } from './shared/FormStatusIndicator';
import { PreviewOrderButton } from './shared/PreviewOrderButton';
import { BackToTopButton } from './shared/BackToTopButton';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';

interface OrderItem {
  id: string;
  packetDescription: string;
  code: string;
  quantity: number;
  price: number;
  category: 'flower' | 'vegetable';
}

interface OrderFormData extends SharedOrderFormData {
  items: OrderItem[];
}

// Product data based on CSV analysis
const livingstonProducts = [
  // $2.99 Flower/Fleur
  { id: '1', name: 'Bachelor Button Choice', code: '143874', price: 2.99, category: 'flower' as const },
  { id: '2', name: 'Cardinal Climber', code: '141002', price: 2.99, category: 'flower' as const },
  { id: '3', name: 'Columbine McKana\'s Giant Mix', code: '143875', price: 2.99, category: 'flower' as const },
  { id: '4', name: 'Cosmos Sensation Picotee', code: '143877', price: 2.99, category: 'flower' as const },
  { id: '5', name: 'Delphinium Pacific Giants Mix', code: '141006', price: 2.99, category: 'flower' as const },
  { id: '6', name: 'Echinicea Coneflower Purple', code: '141003', price: 2.99, category: 'flower' as const },
  { id: '7', name: 'Larkspur Giant Imperial', code: '143131', price: 2.99, category: 'flower' as const },
  { id: '8', name: 'Marigold Petite', code: '143882', price: 2.99, category: 'flower' as const },
  { id: '9', name: 'Poppy Oriental Red', code: '143885', price: 2.99, category: 'flower' as const },
  { id: '10', name: 'Viola Johnny Jump Up', code: '143886', price: 2.99, category: 'flower' as const },
  { id: '11', name: 'Zinnia Cut & Come Again', code: '141025', price: 2.99, category: 'flower' as const },
  { id: '12', name: 'Zinnia Lilliput', code: '141026', price: 2.99, category: 'flower' as const },
  { id: '13', name: 'Zinnia State Fair', code: '138885', price: 2.99, category: 'flower' as const },

  // $3.49 Flower/Fleur
  { id: '14', name: 'Morning Glory Blue Picotee', code: '141029', price: 3.49, category: 'flower' as const },
  { id: '15', name: 'Morning Glory Choice', code: '138828', price: 3.49, category: 'flower' as const },

  // $2.99 Vegetable/Légume
  { id: '16', name: 'Bean Blue Lake (B)', code: '140740', price: 2.99, category: 'vegetable' as const },
  { id: '17', name: 'Bean Blue Lake Stringless (P)', code: '140741', price: 2.99, category: 'vegetable' as const },
  { id: '18', name: 'Bean Bountiful (B)', code: '142073', price: 2.99, category: 'vegetable' as const },
  { id: '19', name: 'Bean Cherokee Wax (B)', code: '142074', price: 2.99, category: 'vegetable' as const },
  { id: '20', name: 'Bean Kentucky Wonder (B)', code: '142075', price: 2.99, category: 'vegetable' as const },
  { id: '21', name: 'Bean Provider (B)', code: '142072', price: 2.99, category: 'vegetable' as const },
  { id: '22', name: 'Bean Topcrop (B)', code: '140750', price: 2.99, category: 'vegetable' as const },
  { id: '23', name: 'Beet Bull\'s Blood', code: '142077', price: 2.99, category: 'vegetable' as const },
  { id: '24', name: 'Cabbage Golden Acre', code: '142078', price: 2.99, category: 'vegetable' as const },
  { id: '25', name: 'Cantaloupe Hearts Of Gold', code: '142080', price: 2.99, category: 'vegetable' as const },
  { id: '26', name: 'Corn Double Sweet', code: '143876', price: 2.99, category: 'vegetable' as const },
  { id: '27', name: 'Cucumber Early Fortune', code: '142083', price: 2.99, category: 'vegetable' as const },
  { id: '28', name: 'Cucumber Marketmore', code: '142084', price: 2.99, category: 'vegetable' as const },
  { id: '29', name: 'Cucumber Muncher Burpless', code: '138983', price: 2.99, category: 'vegetable' as const },
  { id: '30', name: 'Herb Basil Genovese', code: '143878', price: 2.99, category: 'vegetable' as const },
  { id: '31', name: 'Herb Dill Mammoth', code: '143879', price: 2.99, category: 'vegetable' as const },
  { id: '32', name: 'Lettuce Baby Romaine', code: '143880', price: 2.99, category: 'vegetable' as const },
  { id: '33', name: 'Lettuce Bibb', code: '142085', price: 2.99, category: 'vegetable' as const },
  { id: '34', name: 'Lettuce Select Salad Blend', code: '143881', price: 2.99, category: 'vegetable' as const },
  { id: '35', name: 'Lettuce Livingston\'s Mix', code: '142086', price: 2.99, category: 'vegetable' as const },
  { id: '36', name: 'Lettuce Slobolt', code: '142088', price: 2.99, category: 'vegetable' as const },
  { id: '37', name: 'Lettuce Tennis Ball', code: '142089', price: 2.99, category: 'vegetable' as const },
  { id: '38', name: 'Onion Red Wethersfield', code: '142090', price: 2.99, category: 'vegetable' as const },
  { id: '39', name: 'Onion White Lisbon Bunching', code: '142091', price: 2.99, category: 'vegetable' as const },
  { id: '40', name: 'Pea Dwarf Grey Sugar', code: '142092', price: 2.99, category: 'vegetable' as const },
  { id: '41', name: 'Pea Early Frosty', code: '142093', price: 2.99, category: 'vegetable' as const },
  { id: '42', name: 'Pea Oregon Sugar Pod II', code: '143883', price: 2.99, category: 'vegetable' as const },
  { id: '43', name: 'Pepper Bell Color Mix', code: '143884', price: 2.99, category: 'vegetable' as const },
  { id: '44', name: 'Pepper King Of The North', code: '142095', price: 2.99, category: 'vegetable' as const },
  { id: '45', name: 'Spinach America', code: '142098', price: 2.99, category: 'vegetable' as const },
  { id: '46', name: 'Squash Cocozelle', code: '142099', price: 2.99, category: 'vegetable' as const },
  { id: '47', name: 'Squash Table King Acorn', code: '142100', price: 2.99, category: 'vegetable' as const },
  { id: '48', name: 'Tomato Abe Lincoln', code: '142101', price: 2.99, category: 'vegetable' as const },
  { id: '49', name: 'Tomato Baby Roma', code: '142102', price: 2.99, category: 'vegetable' as const },
  { id: '50', name: 'Tomato Brandywine Red', code: '142105', price: 2.99, category: 'vegetable' as const },
  { id: '51', name: 'Tomato Marglobe', code: '142103', price: 2.99, category: 'vegetable' as const },
  { id: '52', name: 'Tomato Red Cherry Large', code: '142106', price: 2.99, category: 'vegetable' as const },
  { id: '53', name: 'Watermelon Black Diamond', code: '142108', price: 2.99, category: 'vegetable' as const },
  { id: '54', name: 'Watermelon Dixie Queen', code: '142109', price: 2.99, category: 'vegetable' as const },
  { id: '55', name: 'Watermelon Moon & Stars Red', code: '142110', price: 2.99, category: 'vegetable' as const },
  { id: '56', name: 'Zucchini Black Beauty', code: '143887', price: 2.99, category: 'vegetable' as const },
  { id: '57', name: 'Zucchini Bush', code: '143888', price: 2.99, category: 'vegetable' as const },

  // $3.49 Vegetable/Légume
  { id: '58', name: 'Pumpkin Big Moon', code: '139068', price: 3.49, category: 'vegetable' as const },
  { id: '59', name: 'Pumpkin Jack-Be-Little', code: '139081', price: 3.49, category: 'vegetable' as const },
  { id: '60', name: 'Pumpkin Livingston\'s Pie', code: '142096', price: 3.49, category: 'vegetable' as const },
];

const FormValidation = styled.div`
  margin-bottom: ${Spacing.lg};
`;

const ProductCategorySection = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacing.lg};
  padding: ${Spacing.md};
  background: ${ThemeColors.primary.light};
  border-radius: ${Spacing.sm};
  
  .category-info {
    display: flex;
    align-items: center;
    gap: ${Spacing.md};
    
    .category-title {
      font-size: ${Typography.fontSize.lg};
      font-weight: ${Typography.fontWeight.bold};
      color: ${ThemeColors.text.primary};
    }
    
    .category-count {
      background: ${ThemeColors.primary.main};
      color: white;
      padding: ${Spacing.xs} ${Spacing.sm};
      border-radius: ${Spacing.sm};
      font-size: ${Typography.fontSize.sm};
      font-weight: ${Typography.fontWeight.semiBold};
    }
  }
  
  .category-price {
    font-size: ${Typography.fontSize.xl};
    font-weight: ${Typography.fontWeight.bold};
    color: ${ThemeColors.primary.main};
  }
`;

export const OptimizedLivingstonOrderForm: React.FC<{ storeId?: string; onShowPreview?: () => void }> = ({ storeId, onShowPreview }) => {
  const { t } = useTranslation();
  
  // Use global order data with cache functionality
  const { 
    orderData, 
    updateOrderData, 
    updateLivingstonItems,
    submitOrder,
    isLoading: globalLoading,
    error: globalError,
    getFormStatus,
    autoSave,
    clearDraft
  } = useGlobalOrderData();
  
  // Fetch customer data from Firebase
  const { customerData, loading: customerLoading, error: customerError } = useCustomerData(storeId || '');
  
  const [selectedItems, setSelectedItems] = useState<{ [key: string]: number }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [collapsedSections, setCollapsedSections] = useState<{ [key: string]: boolean }>({});
  const [formData, setFormData] = useState<SharedOrderFormData>({
    ...getMockCustomerData()
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const prevItemsRef = useRef<string>('');

  // Get form status for the status indicator
  const formStatus = getFormStatus('livingston');

  // Update form data when customer data is loaded
  // NOTE: This should only update once when customerData is first loaded
  // Use a ref to track if we've already synced to prevent loops
  const customerDataSyncedRef = useRef(false);
  useEffect(() => {
    if (customerData && !customerDataSyncedRef.current) {
      setFormData(prev => ({
        ...prev,
        ...customerData
      }));
      customerDataSyncedRef.current = true;
    }
    // Reset when storeId changes
    if (storeId) {
      customerDataSyncedRef.current = false;
    }
  }, [customerData, storeId]);

  // Reset initialization when storeId changes
  useEffect(() => {
    setIsInitialized(false);
    setSelectedItems({});
    prevItemsRef.current = '';
  }, [storeId]);

  // Sync selected items with global order data (only on initial load, once per storeId)
  // Also reset when items become empty (after finalization)
  useEffect(() => {
    if (orderData.livingstonItems === undefined) {
      prevItemsRef.current = '';
      return;
    }
    
    // Create a stable string representation for comparison
    const currentItemsKey = orderData.livingstonItems.length > 0
      ? orderData.livingstonItems
          .map(item => `${item.id}:${item.quantity}`)
          .sort()
          .join('|')
      : '';
    
    // Only process if items actually changed (prevents infinite loops)
    if (currentItemsKey === prevItemsRef.current) return;
    
    const itemsLength = orderData.livingstonItems.length;
    
    if (itemsLength === 0 && isInitialized) {
      // Reset if items were cleared (e.g., after finalization)
      setSelectedItems({});
      setIsInitialized(false); // Allow re-initialization when new items are added
      prevItemsRef.current = currentItemsKey;
      console.log('🔄 Livingston form reset - items cleared');
    } else if (itemsLength > 0 && !isInitialized) {
      // Initialize with items from StoreOrders
      const itemsMap: { [key: string]: number } = {};
      orderData.livingstonItems.forEach(item => {
        // Use exact quantity from StoreOrders - no transformation
        itemsMap[item.id] = item.quantity || 0;
      });
      setSelectedItems(itemsMap);
      setIsInitialized(true);
      prevItemsRef.current = currentItemsKey;
      console.log('✅ Livingston form initialized with', itemsLength, 'items');
    } else if (itemsLength === 0 && !isInitialized) {
      // Items are empty and not initialized - just mark as processed to prevent re-running
      prevItemsRef.current = currentItemsKey;
    }
  }, [orderData.livingstonItems?.length, isInitialized]);

  const handleInputChange = (field: keyof SharedOrderFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    updateOrderData({ [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Trigger auto-save
    autoSave();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const newErrors: { [key: string]: string } = {};
    if (!formData.deliveryDate) {
      newErrors.deliveryDate = 'Delivery date is required';
    }
    if (formData.orderType === null) {
      newErrors.orderType = 'Please select order type';
    }
    
    const hasItems = Object.values(selectedItems).some(qty => qty > 0);
    if (!hasItems) {
      newErrors.items = t('forms.selectAtLeastOneItem');
    }
    
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Convert selected items to order items
      const orderItems: OrderItem[] = Object.entries(selectedItems)
        .filter(([_, quantity]) => quantity > 0)
        .map(([productId, quantity]) => {
          const product = livingstonProducts.find(p => p.id === productId);
          return {
            id: productId,
            packetDescription: product?.name || '',
            code: product?.code || '',
            quantity,
            price: product?.price || 0,
            category: product?.category || 'livingston'
          };
        });

      const finalOrderData = {
        ...formData,
        items: orderItems
      };

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('Order submitted:', finalOrderData);
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          ...getMockCustomerData()
        });
        setSelectedItems({});
        setSubmitStatus('idle');
      }, 3000);
      
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuantityChange = (productId: string, quantity: number) => {
    const newSelectedItems = {
      ...selectedItems,
      [productId]: Math.max(0, quantity)
    };
    setSelectedItems(newSelectedItems);
    
    // Update global order data
    const orderItems: OrderItem[] = Object.entries(newSelectedItems)
      .filter(([_, qty]) => qty > 0)
      .map(([id, qty]) => {
        const product = livingstonProducts.find(p => p.id === id);
        return {
          id,
          packetDescription: product?.name || '',
          code: product?.code || '',
          quantity: qty,
          price: product?.price || 0,
          category: product?.category || 'livingston'
        };
      });
    
    updateLivingstonItems(orderItems);
    // Trigger auto-save
    autoSave();
  };


  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.orderDate) {
      newErrors.orderDate = 'Order date is required';
    }
    if (!formData.deliveryDate) {
      newErrors.deliveryDate = 'Delivery date is required';
    }
    if (formData.orderType === null) {
      newErrors.orderType = 'Please select order type';
    }

    // Check if at least one item is selected
    const hasItems = Object.values(selectedItems).some(qty => qty > 0);
    if (!hasItems) {
      newErrors.items = t('forms.selectAtLeastOneItem');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateTotal = () => {
    return Object.entries(selectedItems).reduce((total, [productId, quantity]) => {
      const product = livingstonProducts.find(p => p.id === productId);
      return total + (product ? product.price * quantity : 0);
    }, 0);
  };

  // Filter and group products
  // NOTE: getTranslatedProductName is used inside the filter but doesn't need to be in dependencies
  // because it's a pure function that doesn't change between renders
  const filteredProducts = useMemo(() => {
    return livingstonProducts.filter(product => {
      const translatedName = getTranslatedProductName(product.name, t);
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           translatedName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.code.includes(searchTerm);
      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
    // Only include searchTerm and selectedCategory in dependencies to prevent loops
    // t function is stable and doesn't need to be in dependencies
  }, [searchTerm, selectedCategory, t]);

  const groupedProducts = useMemo(() => {
    return filteredProducts.reduce((acc, product) => {
      const key = `${product.price.toFixed(2)} ${product.category}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(product);
      return acc;
    }, {} as { [key: string]: typeof livingstonProducts });
  }, [filteredProducts]);

  const selectedItemsCount = Object.values(selectedItems).reduce((sum, qty) => sum + qty, 0);
  const totalAmount = calculateTotal();

  // Calculate global totals for all forms
  // NOTE: This is memoized for performance but should NOT trigger updates
  // The GlobalOrderSummary component will read from orderData directly
  const globalTotals = React.useMemo(() => {
    const allItems = [
      ...(orderData.heritageItems || []),
      ...(orderData.livingstonItems || []),
      ...(orderData.flowerItems || []),
      ...(orderData.vegetableItems || [])
    ];
    
    const totalItems = allItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = allItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    return { totalItems, totalAmount };
    // Remove updateOrderData from dependencies to prevent infinite loops
    // Use stable array length references instead of full arrays
    // GlobalOrderSummary reads from orderData directly via useGlobalOrderData
  }, [
    orderData.heritageItems?.length,
    orderData.livingstonItems?.length,
    orderData.flowerItems?.length,
    orderData.vegetableItems?.length
  ]);

  return (
    <OptimizedFormContainer>
      {/* Form Status Indicator */}
      <FormStatusIndicator
        formType="livingston"
        status={formStatus}
        onReset={clearDraft}
      />
      
      <form onSubmit={handleSubmit}>
        {/* Status Messages */}
        {submitStatus === 'success' && (
          <SuccessMessage>
            <CheckCircle size={20} />
            Order submitted successfully! You will receive a confirmation email shortly.
          </SuccessMessage>
        )}
        
        {submitStatus === 'error' && (
          <ErrorMessage>
            <AlertCircle size={20} />
            There was an error submitting your order. Please try again.
          </ErrorMessage>
          )}
  
          {/* Product Selection */}
          <OptimizedFormSection>
          <SectionTitle 
            icon={Flower} 
            title={t('forms.productSelection')} 
            subtitle={t('forms.livingston.subtitle')}
          />
          
          {/* Search and Filter */}
          <SearchAndFilterContainer>
            <SearchInput>
              <Search className="search-icon" size={20} />
              <input
                className="search-field"
                type="text"
                placeholder={t('forms.searchProducts')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchInput>
            
            <FilterSelect>
              <select
                className="filter-field"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">{t('forms.allCategories')}</option>
                <option value="flower">{t('categories.flower')}</option>
                <option value="vegetable">{t('categories.vegetable')}</option>
              </select>
            </FilterSelect>
          </SearchAndFilterContainer>

          {/* Product Categories */}
          {Object.entries(groupedProducts).map(([categoryKey, products]) => {
            const sectionId = `category-${categoryKey}`;
            const isCollapsed = collapsedSections[sectionId];
            
            return (
              <ProductCategorySection key={categoryKey}>
                <CategoryHeader>
                  <div className="category-info">
                    <div className="category-title">
                      ${products[0].price.toFixed(2)} {products[0].category} Seeds
                    </div>
                    <div className="category-count">
                      {products.length} {products.length === 1 ? t('common.item') : t('common.items')}
                    </div>
                  </div>
                  <div className="category-price">
                    ${products[0].price.toFixed(2)} each
                  </div>
                </CategoryHeader>
                
                <OptimizedProductGrid>
                  {products.map((product) => {
                    const quantity = selectedItems[product.id] || 0;
                    const isSelected = quantity > 0;
                    
                    return (
                      <OptimizedProductCard 
                        key={product.id} 
                        isSelected={isSelected}
                      >
                        <div className="product-header">
                          <div className="product-title">{getTranslatedProductName(product.name, t)}</div>
                          <div className="product-price">${product.price.toFixed(2)}</div>
                        </div>
                        
                        <div className="product-code">Code: {product.code}</div>
                        
                        <CategoryBadge category={product.category} />
                        
                        <QuantityControl
                          value={quantity}
                          onChange={(newQuantity) => handleQuantityChange(product.id, newQuantity)}
                        />
                      </OptimizedProductCard>
                    );
                  })}
                </OptimizedProductGrid>
              </ProductCategorySection>
            );
          })}

          {errors.items && (
            <ErrorMessage>
              <AlertCircle size={20} />
              {errors.items}
            </ErrorMessage>
          )}
        </OptimizedFormSection>

        {/* Global Order Summary */}
        <GlobalOrderSummary />

        {/* Submit Button */}
              {/* Preview Order Button with Validation */}
              <PreviewOrderButton 
                onPreview={() => {
                  if (onShowPreview) {
                    onShowPreview();
                  } else {
                    console.log('Preview functionality not available');
                  }
                }}
              />
      </form>
      
      {/* Back to Top Button */}
      <BackToTopButton scrollThreshold={300} />
    </OptimizedFormContainer>
  );
};
